import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../hooks/useAuth';

interface Props {
  children: React.ReactNode;
  requiredPermission?: string;
  /** Single required role. Takes precedence if both are set. */
  requiredRole?: string;
  /** Allow access if the user has any one of these roles. */
  requiredAnyRole?: string[];
  /** Set on /verify-email and /verify-phone to avoid bouncing in a loop. */
  skipVerificationGate?: boolean;
}

export function AuthGuard({
  children,
  requiredPermission,
  requiredRole,
  requiredAnyRole,
  skipVerificationGate,
}: Props) {
  const { isAuthenticated, isLoading, hasPermission, hasRole, user } = useAuth();
  const location = useLocation();

  if (isLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );

  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;

  // Sequential verification gate: send unverified users to the right step.
  // Pages that ARE the verification flow opt out via skipVerificationGate.
  //
  // SMS phone verification is DISABLED — phone is optional in the simplified
  // signup, OAuth users have no phone, and Vonage credits aren't worth the
  // friction at this stage. The /verify-phone page is still reachable
  // directly (e.g. from /account when a user adds a phone), and the backend
  // endpoints are untouched, so re-enabling is a one-line change here.
  if (!skipVerificationGate && user) {
    const next = encodeURIComponent(location.pathname + location.search);
    if (!user.emailVerified) {
      return <Navigate to={`/verify-email?next=${next}`} replace />;
    }
    // if (!user.phoneVerified) {
    //   return <Navigate to={`/verify-phone?next=${next}`} replace />;
    // }
  }

  if (requiredPermission && !hasPermission(requiredPermission))
    return <Navigate to="/forbidden" replace />;
  if (requiredRole && !hasRole(requiredRole)) return <Navigate to="/forbidden" replace />;
  if (requiredAnyRole && !requiredAnyRole.some(hasRole))
    return <Navigate to="/forbidden" replace />;
  return <>{children}</>;
}
